import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  ManageGeneratorAutocompleteComponent,
  ManageGeneratorAutocompleteCompleteEvent,
} from '../manage-generator/manage-generator-autocomplete';
import {
  ManageUserItem,
  ManageUserLookupOption,
  ManageUserMenuItem,
  ManageUserRightDraftItem,
  ManageUserRoleItem,
  ManageUserPagination,
} from './manage-user.interfaces';
import { createManageUserFormValue } from './manage-user.data';

type ManageUserModalMode = 'create' | 'edit' | 'view';
type ManageUserSortField = 'username' | 'first_name' | 'last_name';
type ManageUserSortOrder = 'asc' | 'desc';
type ManageUserQueryValue = string | number | null | undefined;
type ManageUserQueryParams = Record<string, ManageUserQueryValue>;
type ManageUserAssignmentPayload = {
  generator_id: number | null;
  substation_id: number | null;
};
const EMAIL_PATTERN = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;

interface ManageUserPayload {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  mobile_no?: string;
  role_id?: number | null;
  substation_id?: number | null;
  generator_id?: number | null;
  password?: string;
}

const GEN_USER_ROLE_NAME = 'GEN_USER';
const SUBSTATION_USER_ROLE_NAME = 'SUBSTATION_USER';
const SUB_USER_ROLE_NAME = 'SUB_USER';

@Component({
  selector: 'app-manage-user-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent, ManageGeneratorAutocompleteComponent],
  templateUrl: './manage-user.html',
  styleUrl: './manage-user.scss',
})
export class ManageUserPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;

  protected readonly filterForm: UntypedFormGroup;
  protected readonly userForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected userModalOpen = false;
  protected deleteModalOpen = false;
  protected rightsModalOpen = false;
  protected modalMode: ManageUserModalMode = 'create';
  protected selectedUser: ManageUserItem | null = null;
  protected updatingStatusUserId: number | null = null;
  protected formSubmitted = false;
  protected sortBy: ManageUserSortField | null = null;
  protected sortOrder: ManageUserSortOrder = 'asc';

  protected users: ManageUserItem[] = [];
  protected filteredUsers: ManageUserItem[] = [];
  protected roles: ManageUserRoleItem[] = [];
  protected menus: ManageUserMenuItem[] = [];
  protected generatorOptions: ManageUserLookupOption[] = [];
  protected substationOptions: ManageUserLookupOption[] = [];
  protected filteredGeneratorOptions: ManageUserLookupOption[] = [];
  protected filteredSubstationOptions: ManageUserLookupOption[] = [];
  protected generatorSearchTerm = '';
  protected substationSearchTerm = '';
  protected rightsDraft: ManageUserRightDraftItem[] = [];
  protected pagination: ManageUserPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      role_id: ['all'],
      status: ['all'],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN), Validators.maxLength(255)]],
      first_name: ['', [Validators.required, Validators.maxLength(25)]],
      last_name: ['', [Validators.required, Validators.maxLength(25)]],
      mobile_no: ['', [Validators.required, Validators.maxLength(15), Validators.pattern(/^\d+$/)]],
      role_id: [null, Validators.required],
      substation_id: [null],
      generator_id: [null],
    });
  }

  ngOnInit(): void {
    this.loadUsers(1);

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadUsers(1));

    this.userForm.get('role_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncRoleAssignmentControls());
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        role_id: 'all',
        status: 'all',
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.loadUsers(1);
  }

  protected openAddUserModal(): void {
    this.modalMode = 'create';
    this.selectedUser = null;
    this.userForm.enable({ emitEvent: false });
    this.userForm.reset(createManageUserFormValue());
    this.clearAssignmentSearchTerms();
    this.formSubmitted = false;
    this.userModalOpen = true;
    this.syncRoleAssignmentControls();
  }

  protected openEditUserModal(user: ManageUserItem): void {
    this.modalMode = 'edit';
    this.selectedUser = user;
    this.userForm.enable({ emitEvent: false });
    this.userForm.reset({
      email: user.email,
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      mobile_no: user.mobile_no ?? '',
      role_id: user.role_id,
      substation_id: user.substation_id,
      generator_id: user.generator_id,
    });
    this.generatorSearchTerm = this.getLookupName(this.generatorOptions, user.generator_id, user.generator_name);
    this.substationSearchTerm = this.getLookupName(this.substationOptions, user.substation_id, user.substation_name);
    this.formSubmitted = false;
    this.userModalOpen = true;
    this.syncRoleAssignmentControls();
  }

  protected openViewUserModal(user: ManageUserItem): void {
    this.modalMode = 'view';
    this.selectedUser = user;
    this.userForm.enable({ emitEvent: false });
    this.userForm.reset({
      email: user.email,
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      mobile_no: user.mobile_no ?? '',
      role_id: user.role_id,
      substation_id: user.substation_id,
      generator_id: user.generator_id,
    });
    this.generatorSearchTerm = this.getLookupName(this.generatorOptions, user.generator_id, user.generator_name);
    this.substationSearchTerm = this.getLookupName(this.substationOptions, user.substation_id, user.substation_name);
    this.formSubmitted = false;
    this.userModalOpen = true;
    this.syncRoleAssignmentControls();
    this.userForm.disable({ emitEvent: false });
  }

  protected openDeleteUserModal(user: ManageUserItem): void {
    this.selectedUser = user;
    this.deleteModalOpen = true;
  }

  protected openRightsModal(user: ManageUserItem): void {
    this.selectedUser = user;
    this.rightsDraft = this.buildRightsDraft(user);
    this.rightsModalOpen = true;
  }

  protected closeUserModal(): void {
    this.userModalOpen = false;
    this.selectedUser = null;
    this.modalMode = 'create';
    this.userForm.enable({ emitEvent: false });
    this.userForm.reset(createManageUserFormValue());
    this.clearAssignmentSearchTerms();
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedUser = null;
  }

  protected closeRightsModal(): void {
    this.rightsModalOpen = false;
    this.selectedUser = null;
    this.rightsDraft = [];
  }

  protected saveUser(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.userForm.invalid) {
      this.formSubmitted = true;
      this.userForm.markAllAsTouched();
      return;
    }

    const payload = this.buildUserPayload();
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_USER : AuthEndPoints.EDIT_USER;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeUserModal();
          this.loadUsers();
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedUser) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_USER, ApiMethod.DELETE, { id: this.selectedUser.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadUsers();
        },
      });
  }

  protected saveRights(): void {
    if (!this.selectedUser) {
      return;
    }

    const payload = {
      user_id: this.selectedUser.id,
      menu_rights: this.rightsDraft
        .filter((row) => !this.hasChildRights(row))
        .map((row) => ({
          menu_id: row.menu_id,
          is_read: row.is_read || row.is_write,
          is_write: row.is_read && row.is_write,
        })),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.UPDATE_MENU_RIGHTS, ApiMethod.PATCH, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeRightsModal();
          this.loadUsers();
        },
      });
  }

  protected onReadChange(row: ManageUserRightDraftItem, checked: boolean): void {
    if (this.hasChildRights(row)) {
      this.setDescendantRights(row.menu_id, { is_read: checked, is_write: checked ? undefined : false });
      this.refreshParentRights();
      return;
    }

    row.is_read = checked;
    if (!checked) {
      row.is_write = false;
    }
    this.refreshParentRights();
  }

  protected onWriteChange(row: ManageUserRightDraftItem, checked: boolean): void {
    if (this.hasChildRights(row)) {
      this.setDescendantRights(row.menu_id, { is_read: checked ? true : undefined, is_write: checked });
      this.refreshParentRights();
      return;
    }

    row.is_write = checked;
    if (checked) {
      row.is_read = true;
    }
    this.refreshParentRights();
  }

  protected toggleAllRead(checked: boolean): void {
    this.rightsDraft = this.rightsDraft.map((row) => ({
      ...row,
      is_read: checked,
      is_write: checked ? row.is_write : false,
    }));
  }

  protected toggleAllWrite(checked: boolean): void {
    this.rightsDraft = this.rightsDraft.map((row) => ({
      ...row,
      is_read: checked ? true : row.is_read,
      is_write: checked,
    }));
  }

  protected areAllReadSelected(): boolean {
    return this.rightsDraft.length > 0 && this.rightsDraft.every((row) => row.is_read);
  }

  protected areAllWriteSelected(): boolean {
    return this.rightsDraft.length > 0 && this.rightsDraft.every((row) => row.is_write);
  }

  protected trackByUserId(_: number, user: ManageUserItem): number {
    return user.id;
  }

  protected toggleSort(field: ManageUserSortField): void {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'asc';
    }

    this.loadUsers(1);
  }

  protected isSortAscending(field: ManageUserSortField): boolean {
    return this.sortBy === field && this.sortOrder === 'asc';
  }

  protected isSortDescending(field: ManageUserSortField): boolean {
    return this.sortBy === field && this.sortOrder === 'desc';
  }

  protected trackByRightId(_: number, right: ManageUserRightDraftItem): number {
    return right.menu_id;
  }

  protected isChildRight(right: ManageUserRightDraftItem): boolean {
    return right.depth > 0;
  }

  protected getRoleName(roleId: number | null): string {
    if (roleId === null) {
      return '-';
    }

    return this.roles.find((role) => role.id === roleId)?.display_name ?? '-';
  }

  protected isGeneratorUserRoleSelected(): boolean {
    return this.getSelectedRoleName() === GEN_USER_ROLE_NAME;
  }

  protected isSubstationUserRoleSelected(): boolean {
    return this.isSubstationUserRole(this.getSelectedRoleName());
  }

  protected onGeneratorSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.generatorSearchTerm = event.query ?? '';
    this.filteredGeneratorOptions = this.filterLookupOptions(this.generatorOptions, this.generatorSearchTerm);
    this.syncLookupControlFromSearch('generator_id', this.generatorOptions, this.generatorSearchTerm);
  }

  protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.substationSearchTerm = event.query ?? '';
    this.filteredSubstationOptions = this.filterLookupOptions(this.substationOptions, this.substationSearchTerm);
    this.syncLookupControlFromSearch('substation_id', this.substationOptions, this.substationSearchTerm);
  }

  protected onGeneratorSearchBlur(): void {
    this.normalizeLookupSearchTerm('generator_id', this.generatorOptions, 'generatorSearchTerm');
  }

  protected onSubstationSearchBlur(): void {
    this.normalizeLookupSearchTerm('substation_id', this.substationOptions, 'substationSearchTerm');
  }

  protected getAssignedAssetLabel(user: ManageUserItem): string {
    const roleName = this.normalizeRoleName(user.role_name);
    if (roleName === GEN_USER_ROLE_NAME) {
      return user.generator_name || '-';
    }
    if (this.isSubstationUserRole(roleName)) {
      return user.substation_name || '-';
    }
    return '-';
  }

  protected getRightSummary(user: ManageUserItem): string {
    const activeRights = user.menu_rights.filter((right) => right.is_read || right.is_write);
    if (!activeRights.length) {
      return 'No rights assigned';
    }

    return `${activeRights.length} menu${activeRights.length > 1 ? 's' : ''}`;
  }

  protected handleActiveChange(user: ManageUserItem, value: string): void {
    const isActive = value === 'true';
    if (this.updatingStatusUserId === user.id || user.is_active === isActive) {
      return;
    }

    this.updatingStatusUserId = user.id;
    this.httpSvc
      .requestCall(AuthEndPoints.EDIT_USER, ApiMethod.PATCH, { id: user.id, is_active: isActive })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.updatingStatusUserId = null;
        }),
      )
      .subscribe({
        next: () => {
          this.loadUsers();
        },
        error: () => {
          this.loadUsers();
        },
      });
  }

  protected isStatusUpdating(user: ManageUserItem): boolean {
    return this.updatingStatusUserId === user.id;
  }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.userForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected isMobileNumberInvalid(): boolean {
    const control = this.userForm.get('mobile_no');
    return Boolean(control?.hasError('pattern') && (control.touched || this.formSubmitted));
  }

  protected isEmailInvalid(): boolean {
    const control = this.userForm.get('email');
    return Boolean(control?.hasError('pattern') && (control.touched || this.formSubmitted));
  }

  protected sanitizeMobileNumber(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replace(/\D/g, '');
    if (input.value === digitsOnly) {
      return;
    }

    input.value = digitsOnly;
    this.userForm.get('mobile_no')?.setValue(digitsOnly, { emitEvent: false });
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  private loadUsers(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      role_id: this.filterForm.get('role_id')?.value,
      status: this.filterForm.get('status')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_USERS_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.finishLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          const resolved = this.resolveUsersPayload(response);
          this.users = resolved.users;
          this.filteredUsers = resolved.users;
          this.roles = resolved.roles;
          this.menus = resolved.menus;
          this.generatorOptions = resolved.generators;
          this.substationOptions = resolved.substations;
          this.filterGeneratorAutocomplete(this.generatorSearchTerm);
          this.filterSubstationAutocomplete(this.substationSearchTerm);
          this.pagination = resolved.pagination ?? this.pagination;
          this.finishLoading();
        },
        error: () => {
          this.users = [];
          this.filteredUsers = [];
          this.roles = [];
          this.menus = [];
          this.generatorOptions = [];
          this.substationOptions = [];
          this.filteredGeneratorOptions = [];
          this.filteredSubstationOptions = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
          this.finishLoading();
        },
      });
  }

  private buildUserPayload(): ManageUserPayload {
    const formValue = this.userForm.getRawValue();
    const email = String(formValue.email ?? '').trim();
    const firstName = String(formValue.first_name ?? '').trim();
    const lastName = String(formValue.last_name ?? '').trim();
    const mobileNo = String(formValue.mobile_no ?? '').trim();
    const roleId: number | null = formValue.role_id === null || formValue.role_id === '' ? null : Number(formValue.role_id);
    const assignment = this.getAssignmentPayloadForRole(roleId);

    if (this.modalMode === 'create') {
      return {
        email,
        first_name: firstName,
        last_name: lastName,
        mobile_no: mobileNo,
        role_id: roleId,
        ...assignment,
        password: mobileNo,
      };
    }

    if (!this.selectedUser) {
      return {};
    }

    const payload: ManageUserPayload = {
      id: this.selectedUser.id,
    };

    if (email !== this.selectedUser.email) {
      payload.email = email;
    }

    if (firstName !== (this.selectedUser.first_name ?? '')) {
      payload.first_name = firstName;
    }

    if (lastName !== (this.selectedUser.last_name ?? '')) {
      payload.last_name = lastName;
    }

    if (mobileNo !== (this.selectedUser.mobile_no ?? '')) {
      payload.mobile_no = mobileNo;
    }

    if (roleId !== this.selectedUser.role_id) {
      payload.role_id = roleId;
    }

    if (assignment.generator_id !== this.selectedUser.generator_id) {
      payload.generator_id = assignment.generator_id;
    }

    if (assignment.substation_id !== this.selectedUser.substation_id) {
      payload.substation_id = assignment.substation_id;
    }

    return payload;
  }

  private resolveUsersPayload(response: unknown): {
    users: ManageUserItem[];
    roles: ManageUserRoleItem[];
    menus: ManageUserMenuItem[];
    generators: ManageUserLookupOption[];
    substations: ManageUserLookupOption[];
    pagination: ManageUserPagination | null;
  } {
    const value = this.extractValue(response);
    if (!value || typeof value !== 'object') {
      return { users: [], roles: [], menus: [], generators: [], substations: [], pagination: null };
    }

    const users = Array.isArray((value as { users?: unknown }).users)
      ? (value as { users: unknown[] }).users.map((item) => this.mapUserItem(item))
      : [];
    const roles = Array.isArray((value as { roles?: unknown }).roles)
      ? (value as { roles: unknown[] }).roles.map((item) => this.mapRoleItem(item))
      : [];
    const menus = Array.isArray((value as { menus?: unknown }).menus)
      ? (value as { menus: unknown[] }).menus.map((item) => this.mapMenuItem(item))
      : [];
    const generators = Array.isArray((value as { generators?: unknown }).generators)
      ? (value as { generators: unknown[] }).generators.map((item) => this.mapLookupOption(item))
      : [];
    const substations = Array.isArray((value as { substations?: unknown }).substations)
      ? (value as { substations: unknown[] }).substations.map((item) => this.mapLookupOption(item))
      : [];

    const pagination = this.extractPagination(value);

    return { users, roles, menus, generators, substations, pagination };
  }

  private extractValue(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const rootValue = (response as { value?: unknown }).value;
    if (rootValue && typeof rootValue === 'object') {
      return rootValue;
    }

    return null;
  }

  private mapUserItem(item: unknown): ManageUserItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      id: Number(record['id']),
      serial_no: Number(record['serial_no'] ?? 0),
      username: typeof record['username'] === 'string' ? record['username'] : '',
      email: typeof record['email'] === 'string' ? record['email'] : '',
      mobile_no: typeof record['mobile_no'] === 'string' ? record['mobile_no'] : '',
      first_name: typeof record['first_name'] === 'string' ? record['first_name'] : null,
      last_name: typeof record['last_name'] === 'string' ? record['last_name'] : null,
      is_verified: Boolean(record['is_verified']),
      is_active: Boolean(record['is_active']),
      role_id: record['role_id'] === null || record['role_id'] === undefined ? 0 : Number(record['role_id']),
      role_name: typeof record['role_name'] === 'string' ? record['role_name'] : '-',
      role_display_name: typeof record['role_display_name'] === 'string' ? record['role_display_name'] : '-',
      substation_id: this.toNullableNumber(record['substation_id']),
      substation_name: typeof record['substation_name'] === 'string' ? record['substation_name'] : null,
      generator_id: this.toNullableNumber(record['generator_id']),
      generator_name: typeof record['generator_name'] === 'string' ? record['generator_name'] : null,
      menu_rights: Array.isArray(record['menu_rights'])
        ? record['menu_rights'].map((right) => this.mapRightItem(right))
        : [],
    };
  }

  private mapRoleItem(item: unknown): ManageUserRoleItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      id: Number(record['id']),
      serial_no: Number(record['serial_no'] ?? 0),
      role_name: typeof record['role_name'] === 'string' ? record['role_name'] : '',
      display_name: typeof record['display_name'] === 'string' ? record['display_name'] : '',
    };
  }

  private mapMenuItem(item: unknown): ManageUserMenuItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      id: Number(record['id']),
      serial_no: Number(record['serial_no'] ?? 0),
      parent_menu_id: record['parent_menu_id'] === null || record['parent_menu_id'] === undefined ? null : Number(record['parent_menu_id']),
      menu_name: typeof record['menu_name'] === 'string' ? record['menu_name'] : null,
      menu_url: typeof record['menu_url'] === 'string' ? record['menu_url'] : null,
      menu_code: typeof record['menu_code'] === 'string' ? record['menu_code'] : null,
      order_by: record['order_by'] === null || record['order_by'] === undefined ? null : Number(record['order_by']),
      icon_class: typeof record['icon_class'] === 'string' ? record['icon_class'] : null,
    };
  }

  private mapLookupOption(item: unknown): ManageUserLookupOption {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      id: Number(record['id']),
      name: typeof record['name'] === 'string' ? record['name'] : '',
    };
  }

  private mapRightItem(item: unknown): ManageUserRightDraftItem {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
      menu_id: Number(record['menu_id']),
      serial_no: Number(record['serial_no'] ?? 0),
      menu_name: typeof record['menu_name'] === 'string' ? record['menu_name'] : 'Untitled menu',
      parent_menu_id: record['parent_menu_id'] === null || record['parent_menu_id'] === undefined ? null : Number(record['parent_menu_id']),
      parent_menu_name: typeof record['parent_menu_name'] === 'string' ? record['parent_menu_name'] : null,
      depth: record['depth'] === null || record['depth'] === undefined ? 0 : Number(record['depth']),
      is_read: Boolean(record['is_read']),
      is_write: Boolean(record['is_write']),
    };
  }

  private extractPagination(value: unknown): ManageUserPagination | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const pagination = (value as { pagination?: unknown }).pagination;
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }

    const record = pagination as Record<string, unknown>;
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? 10),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private buildRightsDraft(user: ManageUserItem): ManageUserRightDraftItem[] {
    const menuMap = new Map(this.menus.map((menu) => [menu.id, menu]));
    const rightsByMenuId = new Map(user.menu_rights.map((right) => [right.menu_id, right]));

    const draft = this.sortMenusByHierarchy(this.menus).map((menu) => {
      const existingRight = rightsByMenuId.get(menu.id);
      const depth = this.getMenuDepth(menu.id, menuMap);
      return {
        menu_id: menu.id,
        serial_no: menu.serial_no,
        menu_name: menu.menu_name ?? 'Untitled menu',
        parent_menu_id: menu.parent_menu_id,
        parent_menu_name: menu.parent_menu_id === null ? null : menuMap.get(menu.parent_menu_id)?.menu_name ?? null,
        depth,
        is_read: existingRight?.is_read ?? false,
        is_write: existingRight?.is_write ?? false,
      };
    });

    this.updateParentRights(draft);
    return draft;
  }

  private sortMenusByHierarchy(menus: ManageUserMenuItem[]): ManageUserMenuItem[] {
    const groupedMenus = new Map<number | null, ManageUserMenuItem[]>();

    menus.forEach((menu) => {
      const parentId = menu.parent_menu_id ?? null;
      const group = groupedMenus.get(parentId) ?? [];
      group.push(menu);
      groupedMenus.set(parentId, group);
    });

    const sortGroup = (items: ManageUserMenuItem[]): ManageUserMenuItem[] =>
      [...items].sort((left, right) => {
        const leftOrder = left.order_by ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.order_by ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return (left.menu_name ?? '').localeCompare(right.menu_name ?? '');
      });

    const walk = (parentId: number | null): ManageUserMenuItem[] => {
      const currentLevel = sortGroup(groupedMenus.get(parentId) ?? []);
      return currentLevel.flatMap((menu) => [menu, ...walk(menu.id)]);
    };

    return walk(null);
  }

  private getMenuDepth(menuId: number, menuMap: Map<number, ManageUserMenuItem>): number {
    let depth = 0;
    let currentMenu = menuMap.get(menuId);

    while (currentMenu?.parent_menu_id !== null && currentMenu?.parent_menu_id !== undefined) {
      depth += 1;
      currentMenu = menuMap.get(currentMenu.parent_menu_id);
    }

    return depth;
  }

  private hasChildRights(row: ManageUserRightDraftItem): boolean {
    return this.rightsDraft.some((candidate) => candidate.parent_menu_id === row.menu_id);
  }

  private setDescendantRights(
    parentMenuId: number,
    changes: { is_read?: boolean; is_write?: boolean },
  ): void {
    const childRows = this.rightsDraft.filter((row) => row.parent_menu_id === parentMenuId);
    childRows.forEach((child) => {
      if (changes.is_read !== undefined) {
        child.is_read = changes.is_read;
      }
      if (changes.is_write !== undefined) {
        child.is_write = changes.is_write;
        if (changes.is_write) {
          child.is_read = true;
        }
      }
      this.setDescendantRights(child.menu_id, changes);
    });
  }

  private refreshParentRights(): void {
    this.updateParentRights(this.rightsDraft);
    this.rightsDraft = [...this.rightsDraft];
  }

  private updateParentRights(rows: ManageUserRightDraftItem[]): void {
    const rowsByParentId = new Map<number | null, ManageUserRightDraftItem[]>();
    rows.forEach((row) => {
      const parentRows = rowsByParentId.get(row.parent_menu_id) ?? [];
      parentRows.push(row);
      rowsByParentId.set(row.parent_menu_id, parentRows);
    });

    [...rows]
      .sort((left, right) => right.depth - left.depth)
      .forEach((row) => {
        const childRows = rowsByParentId.get(row.menu_id) ?? [];
        if (!childRows.length) {
          return;
        }

        row.is_read = childRows.every((child) => child.is_read);
        row.is_write = childRows.every((child) => child.is_write);
        if (row.is_write) {
          row.is_read = true;
        }
      });
  }

  private syncRoleAssignmentControls(): void {
    const generatorControl = this.userForm.get('generator_id');
    const substationControl = this.userForm.get('substation_id');
    if (!generatorControl || !substationControl) {
      return;
    }

    if (this.isGeneratorUserRoleSelected()) {
      generatorControl.setValidators([Validators.required]);
      substationControl.clearValidators();
      substationControl.setValue(null, { emitEvent: false });
      this.substationSearchTerm = '';
    } else if (this.isSubstationUserRoleSelected()) {
      substationControl.setValidators([Validators.required]);
      generatorControl.clearValidators();
      generatorControl.setValue(null, { emitEvent: false });
      this.generatorSearchTerm = '';
    } else {
      generatorControl.clearValidators();
      substationControl.clearValidators();
      generatorControl.setValue(null, { emitEvent: false });
      substationControl.setValue(null, { emitEvent: false });
      this.clearAssignmentSearchTerms();
    }

    generatorControl.updateValueAndValidity({ emitEvent: false });
    substationControl.updateValueAndValidity({ emitEvent: false });
    this.filterGeneratorAutocomplete(this.generatorSearchTerm);
    this.filterSubstationAutocomplete(this.substationSearchTerm);
  }

  private getSelectedRoleName(): string {
    const roleId = this.userForm.get('role_id')?.value;
    if (roleId === null || roleId === undefined || roleId === '') {
      return '';
    }

    return this.normalizeRoleName(this.roles.find((role) => role.id === Number(roleId))?.role_name);
  }

  private normalizeRoleName(roleName: string | null | undefined): string {
    return String(roleName ?? '').trim().toUpperCase();
  }

  private isSubstationUserRole(roleName: string): boolean {
    return roleName === SUBSTATION_USER_ROLE_NAME || roleName === SUB_USER_ROLE_NAME;
  }

  private clearAssignmentSearchTerms(): void {
    this.generatorSearchTerm = '';
    this.substationSearchTerm = '';
    this.filteredGeneratorOptions = this.generatorOptions;
    this.filteredSubstationOptions = this.substationOptions;
  }

  private filterGeneratorAutocomplete(query: string): void {
    this.filteredGeneratorOptions = this.filterLookupOptions(this.generatorOptions, query);
  }

  private filterSubstationAutocomplete(query: string): void {
    this.filteredSubstationOptions = this.filterLookupOptions(this.substationOptions, query);
  }

  private filterLookupOptions(options: ManageUserLookupOption[], query: string): ManageUserLookupOption[] {
    const normalizedQuery = String(query ?? '').trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) => option.name.toLowerCase().includes(normalizedQuery));
  }

  private syncLookupControlFromSearch(controlName: 'generator_id' | 'substation_id', options: ManageUserLookupOption[], query: string): void {
    const matchedOption = this.findLookupOptionByName(options, query);
    this.userForm.get(controlName)?.setValue(matchedOption?.id ?? null, { emitEvent: false });
  }

  private normalizeLookupSearchTerm(
    controlName: 'generator_id' | 'substation_id',
    options: ManageUserLookupOption[],
    searchTermKey: 'generatorSearchTerm' | 'substationSearchTerm',
  ): void {
    const controlValue = this.toNullableNumber(this.userForm.get(controlName)?.value);
    const selectedOption = controlValue === null ? null : options.find((option) => option.id === controlValue);
    this[searchTermKey] = selectedOption?.name ?? '';
  }

  private findLookupOptionByName(options: ManageUserLookupOption[], query: string): ManageUserLookupOption | null {
    const normalizedQuery = String(query ?? '').trim().toLowerCase();
    if (!normalizedQuery) {
      return null;
    }

    return options.find((option) => option.name.trim().toLowerCase() === normalizedQuery) ?? null;
  }

  private getLookupName(options: ManageUserLookupOption[], id: number | null, fallbackName: string | null): string {
    if (id === null) {
      return '';
    }

    return options.find((option) => option.id === id)?.name ?? fallbackName ?? '';
  }

  private getAssignmentPayloadForRole(roleId: number | null): ManageUserAssignmentPayload {
    const roleName = this.normalizeRoleName(this.roles.find((role) => role.id === roleId)?.role_name);
    if (roleName === GEN_USER_ROLE_NAME) {
      return {
        generator_id: this.toNullableNumber(this.userForm.get('generator_id')?.value),
        substation_id: null,
      };
    }

    if (this.isSubstationUserRole(roleName)) {
      return {
        generator_id: null,
        substation_id: this.toNullableNumber(this.userForm.get('substation_id')?.value),
      };
    }

    return {
      generator_id: null,
      substation_id: null,
    };
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private buildQueryString(params: ManageUserQueryParams): string {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === 'all') {
        return;
      }

      queryParams.set(key, String(value));
    });

    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return 'No records';
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `Showing ${start} to ${end} of ${this.pagination.total_records} results`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (!totalPages) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages: number[] = [];
    const addPage = (pageNo: number): void => {
      if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
        pages.push(pageNo);
      }
    };

    addPage(1);

    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);

    if (windowStart > 2) {
      pages.push(-1);
    }

    for (let page = windowStart; page <= windowEnd; page += 1) {
      addPage(page);
    }

    if (windowEnd < totalPages - 1) {
      pages.push(-1);
    }

    addPage(totalPages);
    return pages;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadUsers(pageNo);
  }

  private applyPasswordValidators(isCreateMode: boolean): void {
    const passwordControl = this.userForm.get('password');
    if (!passwordControl) {
      return;
    }

    if (isCreateMode) {
      passwordControl.setValidators([Validators.required, Validators.minLength(4)]);
    } else {
      passwordControl.clearValidators();
    }

    passwordControl.updateValueAndValidity({ emitEvent: false });
  }

  private finishLoading(): void {
    if (!this.isLoading) {
      return;
    }

    this.isLoading = false;
    this.cdr.detectChanges();
  }
}
